<template>
    <div>
        <app-layout>
            <!-- Header -->
            <template v-slot:header>
                <Header :title="$t('coop_career_counseling_responsible')"
                        :isFilter="true"
                        @filter-div-status="datatable.filterStatus = $event"
                        :actionButtons="headerActionButtons" />
            </template>
            <template v-slot:header-mobile>
                <HeaderMobile :title="$t('coop_career_counseling_responsible')"
                              :isFilter="true"
                              @filter-div-status="datatable.filterStatus = $event"
                              :actionButtons="headerActionButtons" />
            </template>

            <!-- Filters -->
            <datatable-filter v-show="datatable.filterStatus"
                              :exportExcel="true"
                              :jsonExcelButton="true"
                              :jsonExcelData="datatable.rows"
                              :jsonExcelFields="excelFields"
                              @filter="filter"
                              @filterClear="filterClear">
                <b-row>
                    <b-col md="3" sm="6" class="mb-2">
                        <b-form-group :label="$t('student_number')">
                            <b-form-input v-model="datatable.queryParams.filter.student_number" />
                        </b-form-group>
                    </b-col>
                    <b-col md="3" sm="6" class="mb-2">
                        <b-form-group :label="$t('coop_faculty')">
                            <b-form-input v-model="datatable.queryParams.filter.faculty" />
                        </b-form-group>
                    </b-col>
                    <b-col md="3" sm="6" class="mb-2">
                        <b-form-group :label="$t('coop_department')">
                            <b-form-input v-model="datatable.queryParams.filter.department" />
                        </b-form-group>
                    </b-col>
                    <b-col md="3" sm="6" class="mb-2">
                        <b-form-group :label="$t('coop_program')">
                            <b-form-input v-model="datatable.queryParams.filter.program" />
                        </b-form-group>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col md="3" sm="6" class="mb-2">
                        <b-form-group :label="$t('coop_student_group')">
                            <b-form-input v-model="datatable.queryParams.filter.student_group" />
                        </b-form-group>
                    </b-col>
                    <b-col md="3" sm="6" class="mb-2">
                        <b-form-group :label="$t('name')">
                            <b-form-input v-model="datatable.queryParams.filter.student_name" />
                        </b-form-group>
                    </b-col>
                    <b-col md="3" sm="6" class="mb-2">
                        <b-form-group :label="$t('surname')">
                            <b-form-input v-model="datatable.queryParams.filter.student_surname" />
                        </b-form-group>
                    </b-col>
                    <b-col md="3" sm="6" class="mb-2">
                        <b-form-group :label="$t('status')">
                            <b-form-select v-model="datatable.queryParams.filter.status" :options="statusFilterOptions">
                                <template #first>
                                    <b-form-select-option :value="null">-- {{ $t('status') }} --</b-form-select-option>
                                </template>
                            </b-form-select>
                        </b-form-group>
                    </b-col>
                </b-row>
            </datatable-filter>

            <!-- Datatable -->
            <datatable :isLoading.sync="datatable.isLoading"
                       :columns="datatable.columns"
                       :rows="datatable.rows"
                       :total="datatable.total"
                       :queryParams="datatable.queryParams"
                       :lineNumbers="false"
                       @on-page-change="onPageChange"
                       @on-sort-change="onSortChange"
                       @on-per-page-change="onPerPageChange"
                       v-show="datatable.showTable"
                       :title="$t('coop_my_applications')"
            />
        </app-layout>

        <!-- Columns Toggle Modal -->
        <CommonModal ref="columnsModal" size="md" :onHideOnlyX="true">
            <template v-slot:CommonModalTitle>
                {{ $t('coop_columns') }}
            </template>
            <template v-slot:CommonModalContent>
                <div class="p-2">
                    <div v-for="col in togglableColumns" :key="col.field" class="mb-3">
                        <b-form-checkbox :checked="!col.hidden" @change="toggleColumnVisibility(col)">
                            {{ col.label }}
                        </b-form-checkbox>
                    </div>
                </div>
            </template>
        </CommonModal>

        <!-- Edit Calendar Modal -->
        <CommonModal ref="editCalendarModal" size="lg" :onHideOnlyX="true">
            <template v-slot:CommonModalTitle>
                {{ $t('coop_edit_calendar') }}
            </template>
            <template v-slot:CommonModalContent>
                <div class="p-3">
                    <b-form @submit.prevent="saveCalendarSettings">
                        <!-- Days Configuration -->
                        <h6 class="font-weight-bold mb-3">{{ $t('coop_available_working_days') }}</h6>
                        <b-form-group class="mb-4">
                            <b-form-checkbox-group v-model="calendarForm.active_days" :options="daysOptions" name="active_days" />
                        </b-form-group>

                        <!-- Hours Configuration -->
                        <h6 class="font-weight-bold mb-3">{{ $t('coop_appointment_time') }}</h6>
                        <b-form-group class="mb-4">
                            <b-form-checkbox-group v-model="calendarForm.active_hours" :options="hoursOptions" name="active_hours" />
                        </b-form-group>

                        <b-button type="submit" variant="primary" class="px-4 mt-2">
                            {{ $t('save') }}
                        </b-button>
                    </b-form>
                </div>
            </template>
        </CommonModal>

        <!-- Status Update Modal -->
        <CommonModal ref="statusUpdateModal" size="md" :onHideOnlyX="true">
            <template v-slot:CommonModalTitle>
                {{ $t('coop_update_status') }}
            </template>
            <template v-slot:CommonModalContent>
                <div class="p-2" v-if="selectedRow">
                    <b-form-group :label="$t('status')" class="font-weight-bold">
                        <b-form-select v-model="selectedRowStatus" :options="statusOptions" />
                    </b-form-group>
                    <b-button variant="primary" class="px-4 mt-2" @click="saveStatusUpdate">
                        {{ $t('save') }}
                    </b-button>
                </div>
            </template>
        </CommonModal>

        <!-- Reschedule Calendar Modal (Görüntüle) -->
        <CommonModal ref="rescheduleModal" size="xl" :onHideOnlyX="true">
            <template v-slot:CommonModalTitle>
                {{ $t('coop_appointment_calendar') }}
            </template>
            <template v-slot:CommonModalContent>
                <div class="p-2" v-if="rescheduleForm.id">
                    <!-- Randevu Takvimi Container -->
                    <div class="calendar-container p-4 rounded mb-4" style="background-color: #f7f3ed; border: 1px solid #e6dfd5;">
                        <div class="d-flex justify-content-between align-items-center mb-4">
                            <!-- Week Navigation -->
                            <div class="d-flex align-items-center">
                                <b-button variant="light"
                                          size="sm"
                                          class="rounded-circle shadow-sm border mr-3"
                                          style="width: 32px; height: 32px; padding: 0;"
                                          @click="navigateRescheduleWeek(-1)">
                                    <i class="ri-arrow-left-s-line" style="font-size: 1.2rem; line-height: 1;"></i>
                                </b-button>
                                <span class="font-weight-bold text-secondary">{{ getRescheduleWeekRangeLabel() }}</span>
                                <b-button variant="light"
                                          size="sm"
                                          class="rounded-circle shadow-sm border ml-3"
                                          style="width: 32px; height: 32px; padding: 0;"
                                          @click="navigateRescheduleWeek(1)">
                                    <i class="ri-arrow-right-s-line" style="font-size: 1.2rem; line-height: 1;"></i>
                                </b-button>
                            </div>
                        </div>

                        <!-- Calendar Grid -->
                        <b-row class="text-center mx-0 bg-white p-3 rounded shadow-sm">
                            <b-col v-for="day in rescheduleWeekDays"
                                   :key="day.dateStr"
                                   class="px-1 py-2 border-right last-no-border"
                                   style="min-width: 110px;">
                                <div class="font-weight-bold text-dark mb-1" style="font-size: 0.9rem;">
                                    {{ day.dayName }}
                                </div>
                                <div class="text-muted mb-3" style="font-size: 0.8rem;">
                                    {{ day.dateLabel }}
                                </div>

                                <!-- Slots -->
                                <div class="d-flex flex-column align-items-center">
                                    <b-button v-for="slot in hoursOptionsList"
                                              :key="slot"
                                              size="sm"
                                              :variant="isRescheduleSlotSelected(day.dateStr, slot) ? 'success' : 'light'"
                                              class="w-100 mb-2 py-2 font-weight-bold shadow-sm slot-btn"
                                              :style="getRescheduleSlotStyle(day.dateStr, slot)"
                                              @click="selectRescheduleSlot(day.dateStr, slot)">
                                        {{ slot }}
                                    </b-button>
                                </div>
                            </b-col>
                        </b-row>
                    </div>

                    <!-- Actions -->
                    <div class="text-center">
                        <b-button variant="primary"
                                  size="lg"
                                  class="px-5 mr-3 font-weight-bold shadow"
                                  @click="saveReschedule">
                            {{ $t('save') }}
                        </b-button>
                    </div>
                </div>
            </template>
        </CommonModal>
    </div>
</template>

<script>
    import AppLayout from "@/layouts/AppLayout";
    import Header from "@/layouts/AppLayout/Header";
    import HeaderMobile from "@/layouts/AppLayout/HeaderMobile";
    import Datatable from "@/components/datatable/Datatable";
    import DatatableFilter from "@/components/datatable/DatatableFilter";
    import CommonModal from "@/components/elements/CommonModal";
    import CoopCounselingService from "@/services/CoopCounselingService";
    import moment from "moment";
    import qs from "qs";

    export default {
        name: "CareerCounselingAdmin",
        components: {
            AppLayout,
            Header,
            HeaderMobile,
            Datatable,
            DatatableFilter,
            CommonModal
        },
        data() {
            return {
                selectedRow: null,
                selectedRowStatus: "",
                calendarForm: {
                    active_days: [],
                    active_hours: []
                },
                rescheduleForm: {
                    id: null,
                    date: "",
                    time: ""
                },
                rescheduleWeekStart: null,
                excelFields: {
                    "Öğrenci No": "student_number",
                    "Ad": "student_name",
                    "Soyad": "student_surname",
                    "Fakülte": "faculty",
                    "Bölüm": "department",
                    "Program": "program",
                    "Öğrenci Grubu": "student_group",
                    "Kariyer Danışmanı": "counselor_name",
                    "Randevu Tarihi": "appointment_date",
                    "Randevu Saati": "appointment_time",
                    "Durum": "status"
                },
                statusFilterOptions: [
                    { value: "Bekliyor", text: "Bekliyor" },
                    { value: "Onaylandı", text: "Onaylandı" },
                    { value: "Reddedildi", text: "Reddedildi" }
                ],
                statusOptions: [
                    { value: "Bekliyor", text: "Bekliyor" },
                    { value: "Onaylandı", text: "Onaylandı" },
                    { value: "Reddedildi", text: "Reddedildi" }
                ],
                daysOptions: [
                    { value: "Pazartesi", text: "Pazartesi" },
                    { value: "Salı", text: "Salı" },
                    { value: "Çarşamba", text: "Çarşamba" },
                    { value: "Perşembe", text: "Perşembe" },
                    { value: "Cuma", text: "Cuma" }
                ],
                hoursOptions: [
                    { value: "09:00", text: "09:00" },
                    { value: "10:00", text: "10:00" },
                    { value: "11:00", text: "11:00" },
                    { value: "12:00", text: "12:00" },
                    { value: "13:00", text: "13:00" },
                    { value: "14:00", text: "14:00" },
                    { value: "15:00", text: "15:00" },
                    { value: "16:00", text: "16:00" },
                    { value: "17:00", text: "17:00" }
                ],
                hoursOptionsList: ["09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00"],
                datatable: {
                    isLoading: false,
                    filterStatus: true,
                    columns: [
                        {
                            label: "",
                            field: "buttons",
                            html: true,
                            hidden: false,
                            tdClass: "p-0 text-center w-40 align-middle",
                            buttons: [
                                {
                                    text: this.$t("coop_update_status"),
                                    class: "ri-edit-line text-primary",
                                    callback: (row) => {
                                        this.openStatusUpdateModal(row);
                                    }
                                },
                                {
                                    text: this.$t("view"),
                                    class: "ri-eye-line text-info",
                                    callback: (row) => {
                                        this.openRescheduleModal(row);
                                    }
                                },
                                {
                                    text: this.$t("coop_delete_application"),
                                    class: "ri-delete-bin-line text-danger",
                                    callback: (row) => {
                                        this.deleteApplication(row.id);
                                    }
                                }
                            ]
                        },
                        {
                            label: this.toUpperCase("student_number"),
                            field: "student_number",
                            hidden: false
                        },
                        {
                            label: this.toUpperCase("name"),
                            field: "student_name",
                            hidden: false
                        },
                        {
                            label: this.toUpperCase("surname"),
                            field: "student_surname",
                            hidden: false
                        },
                        {
                            label: this.toUpperCase("coop_faculty"),
                            field: "faculty",
                            hidden: false
                        },
                        {
                            label: this.toUpperCase("coop_department"),
                            field: "department",
                            hidden: false
                        },
                        {
                            label: this.toUpperCase("status"),
                            field: "status",
                            hidden: false
                        }
                    ],
                    rows: [],
                    total: 0,
                    showTable: false,
                    queryParams: {
                        filter: {
                            student_number: "",
                            faculty: "",
                            department: "",
                            program: "",
                            student_group: "",
                            student_name: "",
                            student_surname: "",
                            status: null
                        },
                        sort: "id",
                        page: 1,
                        limit: 20
                    }
                }
            };
        },
        computed: {
            headerActionButtons() {
                return [
                    {
                        text: this.$t("coop_edit_calendar"),
                        callback: this.openEditCalendarModal
                    },
                    {
                        text: this.$t("coop_columns"),
                        callback: this.openColumnsModal
                    }
                ];
            },
            togglableColumns() {
                // Return all columns except actions (buttons)
                return this.datatable.columns.filter(col => col.field !== "buttons");
            },
            rescheduleWeekDays() {
                const days = [];
                if (!this.rescheduleWeekStart) return [];
                const start = this.rescheduleWeekStart.clone();
                const currentLocale = this.$i18n.locale === 'tr' ? 'tr' : 'en';
                for (let i = 0; i < 7; i++) {
                    const dayMoment = start.clone().add(i, 'days').locale(currentLocale);
                    days.push({
                        dateStr: dayMoment.format('YYYY-MM-DD'),
                        dayName: dayMoment.format('dddd'),
                        dateLabel: dayMoment.format('D MMMM'),
                        momentObj: dayMoment
                    });
                }
                return days;
            }
        },
        methods: {
            onPageChange(params) {
                this.datatable.queryParams.page = params.currentPage;
                this.getRows();
            },
            onPerPageChange(params) {
                this.datatable.queryParams.limit = params.currentPerPage;
                this.datatable.queryParams.page = 1;
                this.getRows();
            },
            onSortChange(params) {
                const sortType = params[0].type == "desc" ? "-" : "";
                this.datatable.queryParams.sort = sortType + params[0].field;
                this.getRows();
            },
            filter() {
                this.datatable.queryParams.page = 1;
                this.getRows();
            },
            filterClear() {
                this.datatable.queryParams.filter = {
                    student_number: "",
                    faculty: "",
                    department: "",
                    program: "",
                    student_group: "",
                    student_name: "",
                    student_surname: "",
                    status: null
                };
                this.getRows();
            },
            getRows() {
                this.datatable.showTable = true;
                this.datatable.isLoading = true;

                const config = {
                    params: {
                        ...this.datatable.queryParams
                    },
                    paramsSerializer: (params) => qs.stringify(params, { encode: false })
                };

                return CoopCounselingService.getAll(config)
                    .then((response) => {
                        this.datatable.rows = response.data.data;
                        this.datatable.total = response.data.pagination.total;
                    })
                    .finally(() => {
                        this.datatable.isLoading = false;
                    });
            },
            openColumnsModal() {
                this.$refs.columnsModal.$refs.commonModal.show();
            },
            toggleColumnVisibility(col) {
                col.hidden = !col.hidden;
            },
            openEditCalendarModal() {
                CoopCounselingService.getCalendarConfig().then((res) => {
                    this.calendarForm = { ...res.data };
                    this.$refs.editCalendarModal.$refs.commonModal.show();
                });
            },
            saveCalendarSettings() {
                CoopCounselingService.saveCalendarConfig(this.calendarForm).then(() => {
                    this.$toast.success(this.$t("coop_calendar_updated_success"));
                    this.$refs.editCalendarModal.$refs.commonModal.hide();
                });
            },
            openStatusUpdateModal(row) {
                this.selectedRow = row;
                this.selectedRowStatus = row.status;
                this.$refs.statusUpdateModal.$refs.commonModal.show();
            },
            saveStatusUpdate() {
                CoopCounselingService.updateStatus(this.selectedRow.id, this.selectedRowStatus).then(() => {
                    this.$toast.success(this.$t("coop_status_updated_success"));
                    this.$refs.statusUpdateModal.$refs.commonModal.hide();
                    this.getRows();
                });
            },
            openRescheduleModal(row) {
                this.rescheduleForm = {
                    id: row.id,
                    date: row.appointment_date,
                    time: row.appointment_time
                };
                // Parse date relative to the appointment date
                this.rescheduleWeekStart = moment(row.appointment_date, 'DD.MM.YYYY').clone().subtract(3, 'days');
                this.$refs.rescheduleModal.$refs.commonModal.show();
            },
            navigateRescheduleWeek(direction) {
                this.rescheduleWeekStart = this.rescheduleWeekStart.clone().add(direction * 7, 'days');
            },
            getRescheduleWeekRangeLabel() {
                if (this.rescheduleWeekDays.length === 0) return '';
                const start = this.rescheduleWeekDays[0].momentObj;
                const end = this.rescheduleWeekDays[6].momentObj;
                return `${start.format('D MMM')} - ${end.format('D MMM YYYY')}`;
            },
            selectRescheduleSlot(dateStr, time) {
                this.rescheduleForm.date = moment(dateStr).format('DD.MM.YYYY');
                this.rescheduleForm.time = time;
            },
            isRescheduleSlotSelected(dateStr, time) {
                const formattedDate = moment(dateStr).format('DD.MM.YYYY');
                return this.rescheduleForm.date === formattedDate && this.rescheduleForm.time === time;
            },
            getRescheduleSlotStyle(dateStr, time) {
                if (this.isRescheduleSlotSelected(dateStr, time)) {
                    return {
                        backgroundColor: '#20c997',
                        borderColor: '#20c997',
                        color: '#ffffff'
                    };
                }
                return {
                    backgroundColor: '#ffffff',
                    color: '#6c757d',
                    border: '1px solid #dee2e6'
                };
            },
            saveReschedule() {
                CoopCounselingService.reschedule(
                    this.rescheduleForm.id,
                    this.rescheduleForm.date,
                    this.rescheduleForm.time
                ).then(() => {
                    this.$toast.success(this.$t("coop_reschedule_success"));
                    this.$refs.rescheduleModal.$refs.commonModal.hide();
                    this.getRows();
                });
            },
            deleteApplication(id) {
                CoopCounselingService.remove(id).then(() => {
                    this.$toast.success(this.$t("coop_application_deleted_success"));
                    this.getRows();
                });
            }
        },
        created() {
            this.getRows();
        }
    };
</script>

<style scoped>
    .last-no-border:last-child {
        border-right: none !important;
    }
    .slot-btn:hover {
        opacity: 0.9;
    }
</style>
